import { useRef, useMemo, useState, useCallback, type InputHTMLAttributes, type RefObject } from 'react';
import { Pipette, AlertTriangle, LayoutGrid } from 'lucide-react';
import { colorEngine } from '../../lib/colorEngine';
import { useColorContext } from '../../contexts/ColorContext';
import { ColorPickerPopover } from './ColorPickerPopover';

function getFormatLabel(value: string): string | null {
  const v = value.trim();
  if (!v) return null;
  if (/^#([0-9a-f]{3,4}){1,2}$/i.test(v)) return 'HEX';
  if (/^#([0-9a-f]{8})$/i.test(v)) return 'HEX';
  if (/^rgb\(/i.test(v)) return 'RGB';
  if (/^rgba\(/i.test(v)) return 'RGBA';
  if (/^hsl\(/i.test(v)) return 'HSL';
  if (/^hsla\(/i.test(v)) return 'HSLA';
  if (/^oklch\(/i.test(v)) return 'OKLCH';
  if (/^oklab\(/i.test(v)) return 'OKLAB';
  if (/^lab\(/i.test(v)) return 'CIELAB';
  if (/^lch\(/i.test(v)) return 'CIELCH';
  if (/^hsv\(/i.test(v)) return 'HSV';
  if (/^hwb\(/i.test(v)) return 'HWB';
  if (/^cmyk\(/i.test(v)) return 'CMYK';
  if (/^color\(display-p3/i.test(v)) return 'Display-P3';
  if (/^color\(rec2020/i.test(v)) return 'Rec.2020';
  // Could be a CSS named color
  return null;
}

export interface ColorInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  /** Current input value */
  value: string;

  /** Callback when value changes */
  onChange: (value: string) => void;

  /** Compact variant for use inside tool panels */
  compact?: boolean;

  /** Label text displayed above/beside the input */
  label?: string;

  /** Show a "Use active color" shortcut button */
  onSelectFromGlobal?: boolean;

  /** Hide the status line below the input */
  hideStatus?: boolean;

  /** External ref to the input element */
  inputRef?: RefObject<HTMLInputElement | null>;
}

/**
 * Shared color input component extracted from ContextBar.
 *
 * Provides:
 *  - Format detection feedback
 *  - Validation + error display
 *  - EyeDropper integration (with fallback for unsupported browsers)
 *  - Swatch preview with native color picker
 *  - Controlled input via `value` + `onChange`
 */
export function ColorInput({
  value,
  onChange,
  compact = false,
  label,
  onSelectFromGlobal = false,
  hideStatus = false,
  inputRef: externalRef,
  className = '',
  placeholder = 'hex, rgb(), hsl(), oklch(), name…',
  ...rest
}: ColorInputProps) {
  const internalRef = useRef<HTMLInputElement>(null);
  const ref = externalRef ?? internalRef;

  // Only destructure what's still needed from global context:
  // openEyeDropper, eyeDropperSupported, and result for the "Use active color" button
  const {
    result,
    openEyeDropper,
    eyeDropperSupported,
  } = useColorContext();

  // 1.3 – Local parsing via useMemo
  const localParsed = useMemo(() => value.trim() ? colorEngine.parseColor(value) : null, [value]);

  // 1.4 – Derive swatchColor from localParsed
  const swatchColor = localParsed
    ? `rgb(${localParsed.rgb.r}, ${localParsed.rgb.g}, ${localParsed.rgb.b})`
    : undefined;

  // 1.5 – Derive swatchTitle from localParsed
  const swatchTitle = localParsed?.hex || 'No color';

  // 1.7 – localValid replaces valid
  const localValid = localParsed !== null;

  // 1.8 – localFormatInfo replaces formatInfo
  const localFormatInfo = localParsed ? getFormatLabel(value) : null;

  // 1.9 – localHasError replaces hasError
  const localHasError = value.length > 0 && !localParsed;

  // 1.12 – Native color picker ref
  const nativePickerRef = useRef<HTMLInputElement>(null);

  // 4.9 – Browse popover state
  const [showPicker, setShowPicker] = useState(false);

  const handleEyedropper = useCallback(async () => {
    const color = await openEyeDropper();
    if (color) onChange(color);
  }, [openEyeDropper, onChange]);

  const handleUseGlobal = useCallback(() => {
    // Use the global active color from context
    if (result?.hex) {
      onChange(result.hex);
    }
  }, [result, onChange]);

  const rootClass = compact
    ? `color-input color-input--compact ${className}`
    : `color-input ${className}`;

  return (
    <div className={rootClass}>
      {label && (
        <label className="color-input__label">
          {label}
        </label>
      )}
      <div className="color-input__row">
        {/* 1.13 – Hidden native color picker */}
        <input
          ref={nativePickerRef}
          type="color"
          value={localParsed?.hex || '#000000'}
          onChange={(e) => onChange(e.target.value)}
          style={{
            position: 'absolute',
            width: 0,
            height: 0,
            opacity: 0,
            pointerEvents: 'none',
          }}
          tabIndex={-1}
          aria-hidden="true"
        />

        {/* Swatch preview – 1.6 updated to use swatchColor, 1.14 updated onClick */}
        <div
          className="color-input__swatch"
          style={{
            background: swatchColor ?? 'var(--panel-2, #e5e7eb)',
            cursor: 'pointer',
          }}
          title={swatchTitle}
          onClick={() => nativePickerRef.current?.click()}
        />

        {/* Text input */}
        <div className="color-input__input-wrap">
          <input
            ref={ref}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            spellCheck={false}
            className="color-input__field"
            placeholder={placeholder}
            {...rest}
          />

          {/* 4.10 – Browse button */}
          <button
            className="color-input__pick-btn color-input__pick-btn--browse"
            title="Browse colors"
            onClick={() => setShowPicker(!showPicker)}
            type="button"
          >
            <LayoutGrid size={compact ? 12 : 13} />
          </button>

          {/* EyeDropper button */}
          <button
            className="color-input__pick-btn"
            title={eyeDropperSupported ? 'Pick from screen' : 'EyeDropper not supported in this browser'}
            disabled={!eyeDropperSupported}
            onClick={handleEyedropper}
            type="button"
          >
            {eyeDropperSupported ? (
              <Pipette size={compact ? 12 : 13} />
            ) : (
              <AlertTriangle size={compact ? 12 : 13} className="color-input__pick-btn--unsupported" />
            )}
          </button>
        </div>

        {/* 4.11 – Color Picker Popover */}
        {showPicker && (
          <ColorPickerPopover
            onSelect={(hex) => { onChange(hex); setShowPicker(false); }}
            currentHex={localParsed?.hex}
            onClose={() => setShowPicker(false)}
          />
        )}

        {/* Use global color button */}
        {onSelectFromGlobal && (
          <button
            className="color-input__global-btn"
            title="Use active color from top bar"
            onClick={handleUseGlobal}
            type="button"
            disabled={!result?.hex}
          >
            ↗
          </button>
        )}
      </div>

      {/* Status line – updated to use localValid/localFormatInfo/localHasError */}
      {(localFormatInfo || localHasError) && !compact && !hideStatus && (
        <div className="color-input__status">
          {localFormatInfo && localValid ? (
            <span className="color-input__status--valid">
              ● {localFormatInfo}
            </span>
          ) : localHasError ? (
            <span className="color-input__status--invalid">
              ● invalid color
            </span>
          ) : null}
        </div>
      )}

      {/* Compact error indicator */}
      {localHasError && compact && (
        <span className="color-input__compact-error" title="invalid color">
          ●
        </span>
      )}
    </div>
  );
}
